import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Bookmark, BookmarkCheck, Flag, Loader2, AlertCircle, ChevronLeft, ChevronRight, X, FlaskConical, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, errMsg, label } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const V = {
  primary: "bg-blue text-white hover:bg-blue-hover border border-transparent",
  secondary: "bg-white text-navy border border-field hover:bg-surface",
  community: "bg-white text-purple border-2 border-purple hover:bg-purple/10",
  ghost: "bg-transparent text-link hover:bg-mist border border-transparent",
  danger: "bg-white text-err border border-err hover:bg-err/5",
  navy: "bg-navy text-white hover:bg-navy/90 border border-transparent",
};
export const btn = (v = "primary", size = "md") =>
  `inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${size === "sm" ? "min-h-[44px] px-3 text-sm" : "min-h-[48px] px-5 text-base"} ${V[v]}`;

export function PageHeader({ eyebrow, title, description, children, testid }) {
  return (
    <div className="border-b border-line bg-surface" data-testid={testid || "page-header"}>
      <div className="container-x py-10 md:py-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div className="max-w-3xl">
          {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
          <h1 className="text-3xl md:text-5xl font-extrabold">{title}</h1>
          {description && <p className="mt-4 text-lg text-sub">{description}</p>}
        </div>
        {children && <div className="flex flex-wrap gap-3">{children}</div>}
      </div>
    </div>
  );
}

function FieldWrap({ id, label: lbl, hint, error, required, children }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block font-semibold text-ink">
        {lbl} {required ? <span className="text-err" aria-hidden="true">*</span> : <span className="text-sm font-normal text-sub">(optional)</span>}
      </label>
      {hint && <p id={`${id}-hint`} className="text-sm text-sub">{hint}</p>}
      {children}
      {error && <p id={`${id}-error`} className="flex items-center gap-1.5 text-sm font-medium text-err" data-testid={`${id}-error`}><AlertCircle className="h-4 w-4" aria-hidden="true" />{error}</p>}
    </div>
  );
}
const aria = (id, hint, error) => ({ "aria-invalid": error ? "true" : undefined, "aria-describedby": [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ") || undefined });

export function TextField({ id, label: lbl, hint, error, required, adornment, ...props }) {
  const input = <input id={id} name={id} className={`field ${adornment ? "pr-14" : ""}`} required={required} data-testid={`${id}-input`} {...aria(id, hint, error)} {...props} />;
  return <FieldWrap {...{ id, label: lbl, hint, error, required }}>{adornment ? <div className="relative">{input}<div className="absolute inset-y-0 right-1 flex items-center">{adornment}</div></div> : input}</FieldWrap>;
}
export function TextArea({ id, label: lbl, hint, error, required, rows = 4, ...props }) {
  return <FieldWrap {...{ id, label: lbl, hint, error, required }}><textarea id={id} name={id} rows={rows} className="field" required={required} data-testid={`${id}-input`} {...aria(id, hint, error)} {...props} /></FieldWrap>;
}
export function SelectField({ id, label: lbl, hint, error, required, options, placeholder, ...props }) {
  return (
    <FieldWrap {...{ id, label: lbl, hint, error, required }}>
      <select id={id} name={id} className="field" required={required} data-testid={`${id}-select`} {...aria(id, hint, error)} {...props}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => { const [v, l] = Array.isArray(o) ? o : [o, o]; return <option key={v} value={v}>{l}</option>; })}
      </select>
    </FieldWrap>
  );
}
export function Check({ id, label: lbl, hint, ...props }) {
  return (
    <div className="flex items-start gap-3 min-h-[44px]">
      <input type="checkbox" id={id} className="mt-1 h-5 w-5 accent-[rgb(var(--c-blue))]" data-testid={`${id}-checkbox`} aria-describedby={hint ? `${id}-hint` : undefined} {...props} />
      <label htmlFor={id} className="cursor-pointer"><span className="font-medium">{lbl}</span>{hint && <span id={`${id}-hint`} className="block text-sm text-sub">{hint}</span>}</label>
    </div>
  );
}

export function ErrorSummary({ errors }) {
  const ref = useRef(null);
  const entries = Object.entries(errors || {}).filter(([, v]) => v);
  useEffect(() => { if (entries.length) ref.current?.focus(); }, [entries.length]);
  if (!entries.length) return null;
  return (
    <div ref={ref} tabIndex={-1} role="alert" className="rounded-lg border-2 border-err bg-err/5 p-4" data-testid="form-error-summary">
      <h2 className="text-lg font-bold text-err">There {entries.length === 1 ? "is a problem" : `are ${entries.length} problems`} with this form</h2>
      <ul className="mt-2 list-disc pl-5">
        {entries.map(([k, v]) => <li key={k}><a className="link text-err" href={`#${k}`}>{v}</a></li>)}
      </ul>
    </div>
  );
}

export function Loading({ text = "Loading…" }) {
  return (
    <div role="status" className="flex items-center gap-3 py-12 text-sub" data-testid="loading-state">
      <img
        src="/openpath-logo.png"
        alt=""
        className="h-7 w-7 shrink-0 object-contain"
        aria-hidden="true"
      />
      <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
      {text}
    </div>
  );
}
export function ErrorBox({ message, onRetry }) {
  return (
    <div role="alert" className="rounded-lg border border-err bg-err/5 p-4 text-err flex flex-wrap items-center gap-3" data-testid="error-state">
      <AlertCircle className="h-5 w-5" aria-hidden="true" /><span className="flex-1">{message}</span>
      {onRetry && <button className={btn("secondary", "sm")} onClick={onRetry} data-testid="retry-btn">Try again</button>}
    </div>
  );
}
export function EmptyState({ icon: Icon, title, body, action, testid = "empty-state" }) {
  return (
    <div className="card p-8 md:p-10 text-center bg-surface border-dashed" data-testid={testid}>
      {Icon && <Icon className="mx-auto h-10 w-10 text-sub" aria-hidden="true" />}
      <h3 className="mt-3 text-xl font-semibold">{title}</h3>
      {body && <p className="mt-2 text-sub max-w-xl mx-auto">{body}</p>}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

const TONES = { blue: "bg-mist text-navy border-line", green: "bg-green/10 text-green border-green/40", red: "bg-err/5 text-err border-err/40", purple: "bg-purple/10 text-purple border-purple/40", grey: "bg-surface text-sub border-line", amber: "bg-[#FFF4E5] text-[#7A4100] border-[#E8B66B]" };
export function Badge({ tone = "blue", children, testid, icon: Icon }) {
  return <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-sm font-medium ${TONES[tone]}`}>{Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}{children}</span>;
}
export const SampleBadge = () => <Badge tone="amber" icon={FlaskConical} testid="sample-badge">SAMPLE – not a real opportunity</Badge>;
export const VerifiedBadge = () => <Badge tone="green" icon={ShieldCheck} testid="verified-badge">Verified by OpenPath</Badge>;
const STATUS_TONE = { submitted: "blue", under_review: "blue", interview: "purple", offered: "green", accepted: "green", shortlisted: "purple", rejected: "red", declined: "red", withdrawn: "grey", draft: "grey", pending: "amber", pending_review: "amber", published: "green", open: "green", closed: "grey", archived: "grey", expired: "grey", completed: "green", cancelled: "grey", resolved: "green", dismissed: "grey", active: "green", restricted: "amber", suspended: "red", visible: "green", removed: "red", approved: "green" };
export const StatusBadge = ({ status }) => <Badge tone={STATUS_TONE[status] || "grey"} testid={`status-${status}`}>Status: {label(status)}</Badge>;

export function Pagination({ page, pages, onChange }) {
  if (!pages || pages <= 1) return null;
  return (
    <nav aria-label="Pagination" className="mt-8 flex items-center justify-between gap-3" data-testid="pagination">
      <button className={btn("secondary", "sm")} disabled={page <= 1} onClick={() => onChange(page - 1)} data-testid="pagination-prev"><ChevronLeft className="h-4 w-4" aria-hidden="true" />Previous</button>
      <span aria-live="polite">Page {page} of {pages}</span>
      <button className={btn("secondary", "sm")} disabled={page >= pages} onClick={() => onChange(page + 1)} data-testid="pagination-next">Next<ChevronRight className="h-4 w-4" aria-hidden="true" /></button>
    </nav>
  );
}

export function FilterChips({ chips, onRemove, onClear }) {
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="Active filters" role="group" data-testid="active-filter-chips">
      {chips.map((c) => (
        <button key={c.key} onClick={() => onRemove(c.key)} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-field bg-mist px-3 text-sm font-medium text-navy hover:bg-white" data-testid={`chip-${c.key}`}>
          {c.label}<X className="h-4 w-4" aria-hidden="true" /><span className="sr-only">Remove filter</span>
        </button>
      ))}
      <button onClick={onClear} className="link min-h-[44px] px-2 font-semibold" data-testid="clear-all-filters">Clear all filters</button>
    </div>
  );
}

export function SaveButton({ kind, id, initial = false, size = "sm" }) {
  const { user } = useAuth();
  const loc = useLocation();
  const [saved, setSaved] = useState(initial);
  useEffect(() => setSaved(initial), [initial]);
  const toggle = async () => {
    if (!user) { toast.info("Sign in to save items."); window.location.assign(`/signin?next=${encodeURIComponent(loc.pathname)}`); return; }
    try { const { data } = await api.post("/saved", { kind, item_id: id }); setSaved(data.saved); toast.success(data.saved ? "Saved" : "Removed from saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <button onClick={toggle} aria-pressed={saved} className={btn("secondary", size)} data-testid={`save-${kind}-${id}`}>
      {saved ? <BookmarkCheck className="h-4 w-4 text-green" aria-hidden="true" /> : <Bookmark className="h-4 w-4" aria-hidden="true" />}{saved ? "Saved" : "Save"}
    </button>
  );
}

export function ReportButton({ type, id, small = true }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!reason) { setErr("Choose a reason for the report."); return; }
    try { const { data } = await api.post("/reports", { target_type: type, target_id: id, reason, details }); toast.success(data.message); setOpen(false); setReason(""); setDetails(""); }
    catch (e2) { setErr(errMsg(e2)); }
  };
  if (!user) return <Link to="/signin" className={btn("ghost", "sm")} data-testid={`report-${type}-signin`}><Flag className="h-4 w-4" aria-hidden="true" />Sign in to report</Link>;
  return (
    <>
      <button className={btn("ghost", small ? "sm" : "md")} onClick={() => setOpen(true)} data-testid={`report-${type}-${id}`}><Flag className="h-4 w-4" aria-hidden="true" />Report</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle>Report this {label(type).toLowerCase()}</DialogTitle>
            <DialogDescription>
              Reports are confidential and reviewed by OpenPath moderators.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <SelectField id="report-reason" label="Reason" required value={reason} error={err && !reason ? err : ""} onChange={(e) => setReason(e.target.value)} placeholder="Choose a reason"
              options={["Spam or scam", "Misleading or false information", "Harassment or hate", "Discrimination", "Privacy violation", "Other"]} />
            <TextArea id="report-details" label="Details" rows={3} value={details} onChange={(e) => setDetails(e.target.value)} />
            {err && reason && <ErrorBox message={err} />}
            <div className="flex justify-end gap-3"><button type="button" className={btn("secondary")} onClick={() => setOpen(false)}>Cancel</button><button className={btn("danger")} data-testid="report-submit-btn">Submit report</button></div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function RequireAuth({ roles, children }) {
  const { user, has } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="container-x"><Loading text="Checking your session…" /></div>;
  if (!user) return <Navigate to={`/signin?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (roles && !has(...roles)) {
    return (
      <div className="container-x py-16 max-w-2xl" data-testid="access-denied">
        <h1 className="text-3xl font-bold">This area needs a different role</h1>
        <p className="mt-3 text-sub">This page is for {roles.map(label).join(" or ")} accounts. You can add roles to your account in settings.</p>
        <Link to="/settings?tab=roles" className={`${btn()} mt-6`} data-testid="add-role-link">Manage my roles</Link>
      </div>
    );
  }
  return children;
}

export function DevLink({ link }) {
  if (!link) return null;
  return (
    <div className="rounded-lg border border-[#E8B66B] bg-[#FFF4E5] p-4 text-[#5C3100]" data-testid="dev-email-link">
      <p className="font-semibold">Development preview: email link</p>
      <p className="text-sm">Shown only because development link display is enabled. This will be removed before launch.</p>
      <a href={link} className="link break-all text-[#5C3100]" data-testid="dev-email-link-anchor">{link}</a>
    </div>
  );
}
